import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "@/components/ui/use-toast.ts";
import { Criteria } from "@/types/Criteria.ts";
import { createItem, listItems, removeItem, updateItem } from "@/services/itemService.ts";
import { TableFloatUI } from "@/components/ui/table-floatui.tsx";
import { pathToCriterionName } from "@/utils/criteria-helper.tsx";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";

function getPath(url: string): string {
    const segments = url.split("/");
    return segments.length === 2 ? "languages" : segments[segments.length - 1]; // ['', 'match-criteria']
}

const CriteriaPage = () => {
    const { t } = useTranslation();
    const [items, setItems] = useState<Criteria[]>([]);
    const [itemToDelete, setItemToDelete] = useState<Criteria | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { pathname } = useLocation();
    const path = getPath(pathname);

    const columnMap = {
        name: "Name",
        nameFr: "Name (French)",
    };

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const data = await listItems(path);
                setItems(data);
            } catch (error) {
                console.error(`Error fetching ${path}:`, error);
            }
        };

        fetchItems();
    }, [path]);

    const handleDeleteRequest = useCallback((item: Criteria) => {
        setItemToDelete(item);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!itemToDelete) return;
        setIsDeleting(true);
        try {
            await removeItem(path, itemToDelete.id);
            setItems((prev) => prev.filter((item) => item.id !== itemToDelete.id));
            toast({
                title: t("common.successTitle"),
                description: t("criteria.deleteSuccess", {
                    criterion: t(`criteria.${pathToCriterionName[path]}`),
                }),
            });
            setItemToDelete(null);
        } catch (error) {
            console.error(`Error deleting ${pathToCriterionName[path]}:`, error);
            toast({
                variant: "destructive",
                title: t("common.errorTitle"),
                description: t("common.errorDesc"),
            });
        } finally {
            setIsDeleting(false);
        }
    }, [itemToDelete, path, t]);

    const handleCancelDelete = useCallback(() => {
        setItemToDelete(null);
    }, []);

    const onSave = async (item: Criteria, itemId: number | null = null) => {
        try {
            if (itemId) {
                await updateItem(path, itemId, item);
                toast({
                    title: t("common.successTitle"),
                    description: t("criteria.updateSuccess", {
                        criterion: t(`criteria.${pathToCriterionName[path]}`)
                    }),
                })
            } else {
                await createItem(path, item);
                toast({
                    title: t("common.successTitle"),
                    description: t("criteria.createSuccess", {
                        criterion: t(`criteria.${pathToCriterionName[path]}`)
                    }),
                })
            }
            const data = await listItems(path);
            setItems(data);
        } catch (error) {
            console.error('Error:', itemId ? `updating ${pathToCriterionName[path]}` : `creating ${pathToCriterionName[path]}`, error);
        }
    };

  return (
    <div className="space-y-6">
        <TableFloatUI
          tableItems={items}
          tableTitle={t(`criteria.${pathToCriterionName[path]}`)}
          columns={columnMap}
          saveHandler={onSave}
          deleteHandler={handleDeleteRequest}
        />

        <AlertDialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {itemToDelete && (
                            <>
                                {t("criteria.deleteConfirmDesc")}
                                {(itemToDelete.name || itemToDelete.nameFr) && (
                                    <span className="mt-2 block font-medium text-foreground">
                                        {t("common.delete")}: {itemToDelete.name ?? itemToDelete.nameFr ?? `#${itemToDelete.id}`}
                                    </span>
                                )}
                            </>
                        )}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel onClick={handleCancelDelete} disabled={isDeleting}>
                        {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => {
                            e.preventDefault();
                            handleConfirmDelete();
                        }}
                        disabled={isDeleting}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                        {isDeleting ? t("common.loading") : t("common.delete")}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}

export default CriteriaPage;
